import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import ReactECharts from 'echarts-for-react';
import CustomDropdown from 'components/Dropdown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';

type PieData = {
    name: string;
    value: number;
    count?: number;
};

type KpiWithPieProps = {
    isLoading?: boolean;
    title: string | React.ReactNode;
    number: number | string;
    numberSuffix?: string;
    suffixFontSize?: number | string;
    change?: number;
    changeType?: 'increase' | 'decrease';
    showChangePercentage?: boolean;
    pieData: PieData[];
    colorMapping?: { [key: string]: string };
    color?: string;
    showDollarAmounts?: boolean;
    containerProps?: any;
    hasDropdown?: boolean;
    hasTooltip?: boolean;
    tooltipText?: string;
    dropdownValue?: string;
    dropdownOptions?: { label: string; value: string }[];
    onDropdownChange?: (value: string) => void;
};


const KpiWithPie = ({
    isLoading = false,
    title,
    number,
    numberSuffix,
    suffixFontSize = 20,
    change,
    changeType = 'increase',
    showChangePercentage = false,
    pieData = [],
    colorMapping = {},
    color,
    showDollarAmounts = false,
    containerProps,
    hasDropdown = false,
    hasTooltip = false,
    tooltipText,
    dropdownValue,
    dropdownOptions = [],
    onDropdownChange
}: KpiWithPieProps) => {
    const theme = useTheme();
    const colors = theme.palette;
    const resolvedColor = color || colors.widget?.text?.main;
    const defaultColors = [
        colors.widget?.colors?.color1,
        colors.widget?.colors?.color6,
        colors.widget?.colors?.trendLine,
        colors.widget?.colors?.color4,
        colors.widget?.colors?.color9
    ];

    // Function to determine change color
    const getChangeColor = () => {
        if (change === undefined || change === 0) return colors.widget?.text?.noChange;
        return changeType === 'increase' ? colors.widget?.text?.changeUp : colors.widget?.text?.changeDown;
    };

    const renderChangeIcon = () => {
        if (change === undefined || change === 0) return null;
        return changeType === 'increase' ? <ArrowUpward fontSize="small" sx={{ fontSize: 20 }} /> : <ArrowDownward fontSize="small" sx={{ fontSize: 20 }} />;
    };

    const renderPieSection = () => {
        if (isLoading) {
			return null
		}

        if (pieData.length > 0) {
            return (
                <ReactECharts
                    option={pieChartOption}
                    style={{ width: '100%', height: '100%' }}
                    opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                    notMerge={true}
                    lazyUpdate={true}
                />
            );
        }

        return (
            <Typography variant="bodyText" sx={{ color: colors.widget?.text?.noChange, textAlign: 'center', mt: 4 }}>
                No data
            </Typography>
        );
    };

    const renderKpiSection = () => {
        if (isLoading) {
			return null;
		}

        return (
            <Stack color={resolvedColor} direction="column" alignItems="start" justifyContent="flex-start" sx={{ width: '100%' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={1} sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Stack direction="row" alignItems="center">
                        <Typography variant="widgetTitle" sx={{ color: resolvedColor, display: 'flex', alignItems: 'center', minHeight: '24px' }}>
                            {typeof title === 'string' ? <HighlightedText text={title} /> : title}
                        </Typography>
                        {hasTooltip && tooltipText && <InfoTooltip label={tooltipText} />}
                    </Stack>
                    {hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
                        <CustomDropdown
                            value={dropdownValue}
                            options={dropdownOptions}
                            handleChange={(event: any) => {
                                if (onDropdownChange) {
                                    onDropdownChange(event.target.value);
                                }
                            }}
                            dropdownIcon={KeyboardArrowDownIcon}
                            selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
                            textColor={colors.DropdownV2?.menuItemText}
                            iconColor={colors.DropdownV2?.dropdownIconDefault}
                            bgColor={colors.DropdownV2?.menuItemBackground}
                            borderColor="transparent"
                            hoverBgColor={colors.DropdownV2?.menuItemHover}
                            hoverTextColor={colors.DropdownV2?.menuItemHoverText}
                            selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
                            boxProps={{
                                sx: {
                                    backgroundColor: 'transparent !important',
                                    width: 'fit-content',
                                    minWidth: 'unset',
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'transparent !important',
                                        width: 'fit-content',
                                        minWidth: 'unset'
                                    },
                                    '& .MuiSelect-select': {
                                        fontFamily: 'Montserrat',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        padding: '0 24px 0 8px !important',
                                        minHeight: '28px !important',
                                        width: 'auto',
                                        minWidth: 'unset',
                                        display: 'flex',
                                        alignItems: 'center'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none !important'
                                    },
                                    '& .MuiSelect-icon': {
                                        color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
                                        right: 0
                                    }
                                }
                            }}
                        />
                    )}
                </Stack>
                <Typography
                    variant="metricValue"
                    sx={{
                        color: resolvedColor,
                        mt: 1,
                        mb: showChangePercentage ? 1 : 0,
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1
                    }}
                >
                    {formatNumber(number)}
                    {numberSuffix && (
                        <Typography
                            component="span"
                            variant='metricValue'
                            sx={{
                                lineHeight: 1
                            }}
                        >
                            {numberSuffix}
                        </Typography>
                    )}
                </Typography>
                    <Box>
                        <Box display={showChangePercentage?"flex":"none"} alignItems="center" color={getChangeColor()}>
                            {renderChangeIcon()}
                            <Typography variant="bodyText" ml={0.5} sx={{ color: getChangeColor(), fontSize: 14 }}>
                                {change === 0 ? '0%' : `${change}%`} vs previous year
                            </Typography>
                        </Box>
                    </Box>
            </Stack>
        );
    };

    const formatNumber = (num: number | string): string => {
        if (typeof num === 'string') return num;
        if (num >= 1000000) {
            const millions = num / 1000000;
            // Check if it's a whole number or has decimals
            if (millions % 1 === 0) {
                return millions.toFixed(0) + ' MM';
            }
            return millions.toFixed(2) + ' MM';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'k';
        }
        return num.toString();
    };

    // Calculate total for percentage
    const total = pieData.reduce((sum, item) => sum + item.value, 0);

    const pieChartOption: any = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item' as const,
            backgroundColor: colors.widget?.chart?.tooltipBg,
            borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
            borderWidth: 1,
            textStyle: {
                color: colors.widget?.chart?.tooltipText,
                fontSize: 12
            },
            formatter: (params: any) => {
                const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
                const count = params.data.count;

                // If count exists, show it along with percentage
                if (count !== undefined) {
                    return `${params.name}: ${count.toLocaleString()} (${percent}%)`;
                }
                // Otherwise just show the value and percentage
                return `${params.name}: ${params.value.toLocaleString()} (${percent}%)`;
            }

        },
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal',
            bottom: '0px',
            left: '0px',
            itemWidth: 14,
            itemHeight: 14,
            itemGap: 15,
            textStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 11
            },
            pageTextStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 12,
                fontWeight: 500
            },
            pageIconColor: colors.widget?.chart?.legendActiveUpDownIconColor,
            pageIconInactiveColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
            pageIconSize: 12,
            data: pieData.map(item => item.name)
        },
        series: [
            {
                name: 'Distribution',
                type: 'pie',
                radius: '68%',
                center: ['50%', '45%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 0,
                    borderColor: colors.widget?.background?.card,
                    borderWidth: 0
                },
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (params: any) => {
                        if (showDollarAmounts) {
                            const value = params.value;
                            if (value >= 1000000) {
                                const millions = value / 1000000;
                                return `$${millions.toFixed(2)} MM`;
                            }
                            return `$${value.toLocaleString()}`;
                        }
                        const percent = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0;
                        return `${percent}%`;
                    },
                    color: colors.widget?.chart?.labelColor,
                    fontSize: 12,
                    fontWeight: 'bold'
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 12,
                        fontWeight: 'bold'
                    }
                },
                data: pieData.map((item, index) => ({
                    value: item.value,
                    name: item.name,
                    count: item.count,
                    itemStyle: {
                        color: colorMapping[item.name] || defaultColors[index % defaultColors.length]
                    }
                }))
            }
        ]
    };

    return (
		<Box display="flex" flexGrow={1} height="100%" position="relative" {...containerProps}>
			<Stack
				p={{ xs: 2, sm: 2 }}
				bgcolor={colors.widget?.background?.card}
				fontFamily="Montserrat"
				borderRadius="5px"
				width="100%"
				flexGrow={1}
				sx={{
					flexDirection: 'column',
					justifyContent: 'flex-start',
					overflow: 'hidden',
					...containerProps?.sx
				}}
			>		

                {isLoading ? (
                    <Stack flexGrow={1} justifyContent="center" alignItems="center" width="100%">
                        <LoaderComponent />
                    </Stack>
                ) : (
                    <>
                        {/* Left side - KPI Number */}
                        <Stack justifyContent="flex-start" width="100%">
                            {renderKpiSection()}
                        </Stack>

                        {/* Right side - Pie Chart */}
                        <Stack flexGrow={1} minHeight={0} width="100%">
                            <Box height="100%" minHeight={{ xs: '180px', sm: '190px' }} width="100%">
                                {renderPieSection()}
                            </Box>
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    );
};

export default KpiWithPie;

